/**
 * Atlas scene widgets (Fear counter, progress clocks, …) → Path A board HUD.
 * Reads `state.widgetSettings.widgets` + `state.widgetValues` from .atlasmap.
 */

export const MAX_WIDGETS = 24;

export type SceneWidgetType = "counter" | "clock";

export type SceneWidget = {
  id: string;
  type: SceneWidgetType;
  label: string;
  icon: string | null;
  color: string | null;
  value: number;
  min: number;
  max: number | null;
  segments: number | null;
  showCount: boolean;
  visibleToPlayers: boolean;
  order: number;
  x?: number | null;
  y?: number | null;
  width?: number | null;
  height?: number | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asFiniteNumber(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function asTrimmedString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const t = value.trim();
  return t.length > 0 ? t : null;
}

function normalizeWidgetType(raw: unknown): SceneWidgetType | null {
  if (raw === "counter" || raw === "clock") return raw;
  if (typeof raw === "string") {
    const t = raw.trim().toLowerCase();
    if (t === "counter" || t === "count") return "counter";
    if (t === "clock" || t === "progress" || t === "countdown") return "clock";
  }
  return null;
}

/**
 * Parse Atlas widgetSettings + widgetValues into ordered SceneWidget[].
 * Soft-fails invalid entries (same style as pins/fog).
 */
export function parseAtlasWidgets(
  widgetSettings: unknown,
  widgetValues: unknown,
  extraWidgets?: unknown,
): SceneWidget[] {
  let bag: Record<string, unknown> | null = null;
  if (isRecord(widgetSettings)) {
    if (isRecord(widgetSettings.widgets)) {
      bag = widgetSettings.widgets as Record<string, unknown>;
    } else {
      bag = widgetSettings as Record<string, unknown>;
    }
  }
  if (!bag && isRecord(extraWidgets)) {
    bag = extraWidgets as Record<string, unknown>;
  }
  if (!bag) return [];

  const values = isRecord(widgetValues) ? widgetValues : {};
  const out: SceneWidget[] = [];
  const seen = new Set<string>();

  for (const [key, raw] of Object.entries(bag)) {
    if (!isRecord(raw)) continue;
    if (raw.visible === false) continue;

    const id =
      asTrimmedString(raw.id) ||
      (typeof key === "string" && key.trim() ? key.trim() : null);
    if (!id || seen.has(id)) continue;

    let type = normalizeWidgetType(raw.type);
    if (!type) {
      // Infer from Atlas id prefixes used by EmberWatch pack.
      if (id.startsWith("counter-") || id.includes("fear")) type = "counter";
      else if (id.startsWith("clock-") || id.includes("collapse")) type = "clock";
      else continue;
    }

    const label =
      asTrimmedString(raw.label) ||
      asTrimmedString(raw.name) ||
      id;
    const order = asFiniteNumber(raw.order, out.length);
    const valueFromSettings = asFiniteNumber(raw.value, 0);
    const valueFromBag = values[id];
    const value = asFiniteNumber(valueFromBag, valueFromSettings);

    const min = Math.max(0, asFiniteNumber(raw.min, 0));
    let max: number | null = null;
    if (typeof raw.max === "number" && Number.isFinite(raw.max)) {
      max = Math.max(min, raw.max);
    }
    let segments: number | null = null;
    if (type === "clock") {
      const seg = asFiniteNumber(raw.segments, 6);
      segments = Math.min(24, Math.max(2, Math.round(seg)));
      if (max === null) max = segments;
    } else if (max === null) {
      max = 12;
    }

    const x = typeof raw.x === "number" && Number.isFinite(raw.x) ? raw.x : null;
    const y = typeof raw.y === "number" && Number.isFinite(raw.y) ? raw.y : null;
    const width = typeof raw.width === "number" && Number.isFinite(raw.width) ? raw.width : null;
    const height = typeof raw.height === "number" && Number.isFinite(raw.height) ? raw.height : null;

    const isExplicitlyGm =
      raw.gmOnly === true ||
      raw.visibility === "gm" ||
      raw.visibleToPlayers === false;
    const isExplicitlyPlayer =
      raw.visibleToPlayers === true ||
      raw.visibility === "players" ||
      raw.visibility === "player" ||
      raw.gmOnly === false;

    let visibleToPlayers = true;
    if (isExplicitlyGm) {
      visibleToPlayers = false;
    } else if (isExplicitlyPlayer) {
      visibleToPlayers = true;
    } else if (raw.visibleToPlayers !== undefined) {
      visibleToPlayers = raw.visibleToPlayers === true;
    } else {
      if (/gm|secret|hidden/i.test(id) || /gm|secret|hidden/i.test(label)) {
        visibleToPlayers = false;
      } else {
        visibleToPlayers = true;
      }
    }

    out.push({
      id,
      type,
      label: label.slice(0, 64),
      icon: asTrimmedString(raw.icon),
      color: asTrimmedString(raw.color),
      value: Math.max(min, max !== null ? Math.min(max, value) : value),
      min,
      max,
      segments,
      showCount: raw.showCount !== false,
      visibleToPlayers,
      order,
      x,
      y,
      width,
      height,
    });
    seen.add(id);
    if (out.length >= MAX_WIDGETS) break;
  }

  out.sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
  return out;
}

/** Runtime / scene.v1 widgets array (soft validation). */
export function parseSceneWidgets(
  raw: unknown,
): { ok: true; widgets: SceneWidget[] } | { ok: false; error: string } {
  if (raw === undefined) return { ok: true, widgets: [] };
  if (!Array.isArray(raw)) return { ok: false, error: "widgets must be an array" };
  if (raw.length > MAX_WIDGETS) {
    return { ok: false, error: `Too many widgets (max ${MAX_WIDGETS})` };
  }
  const widgets: SceneWidget[] = [];
  const seen = new Set<string>();
  for (let i = 0; i < raw.length; i++) {
    const w = raw[i];
    if (!isRecord(w)) {
      return { ok: false, error: `widgets[${i}] must be an object` };
    }
    const id = asTrimmedString(w.id);
    if (!id) return { ok: false, error: `widgets[${i}].id is required` };
    if (seen.has(id)) {
      return { ok: false, error: `Duplicate widget id: ${id}` };
    }
    const type = normalizeWidgetType(w.type);
    if (!type) {
      return { ok: false, error: `widgets[${i}].type must be counter or clock` };
    }
    const label = asTrimmedString(w.label) || id;
    const min = Math.max(0, asFiniteNumber(w.min, 0));
    let max: number | null =
      typeof w.max === "number" && Number.isFinite(w.max) ? w.max : null;
    let segments: number | null = null;
    if (type === "clock") {
      const seg = asFiniteNumber(w.segments, 6);
      segments = Math.min(24, Math.max(2, Math.round(seg)));
      if (max === null) max = segments;
    }
    const value = asFiniteNumber(w.value, 0);
    const x = typeof w.x === "number" && Number.isFinite(w.x) ? w.x : null;
    const y = typeof w.y === "number" && Number.isFinite(w.y) ? w.y : null;
    const width = typeof w.width === "number" && Number.isFinite(w.width) ? w.width : null;
    const height = typeof w.height === "number" && Number.isFinite(w.height) ? w.height : null;

    const isExplicitlyGm =
      w.gmOnly === true ||
      w.visibility === "gm" ||
      w.visibleToPlayers === false;
    const isExplicitlyPlayer =
      w.visibleToPlayers === true ||
      w.visibility === "players" ||
      w.visibility === "player" ||
      w.gmOnly === false;

    let visibleToPlayers = true;
    if (isExplicitlyGm) {
      visibleToPlayers = false;
    } else if (isExplicitlyPlayer) {
      visibleToPlayers = true;
    } else if (w.visibleToPlayers !== undefined) {
      visibleToPlayers = w.visibleToPlayers === true;
    } else {
      if (/gm|secret|hidden/i.test(id) || /gm|secret|hidden/i.test(label)) {
        visibleToPlayers = false;
      } else {
        visibleToPlayers = true;
      }
    }

    widgets.push({
      id,
      type,
      label: label.slice(0, 64),
      icon: asTrimmedString(w.icon),
      color: asTrimmedString(w.color),
      value: Math.max(min, max !== null ? Math.min(max, value) : value),
      min,
      max,
      segments,
      showCount: w.showCount !== false,
      visibleToPlayers,
      order: asFiniteNumber(w.order, i),
      x,
      y,
      width,
      height,
    });
    seen.add(id);
  }
  widgets.sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
  return { ok: true, widgets };
}

export function widgetsForViewer(
  widgets: SceneWidget[],
  role: "gm" | "player",
): SceneWidget[] {
  if (role === "gm") return widgets.map((w) => ({ ...w }));
  return widgets.filter((w) => w.visibleToPlayers).map((w) => ({ ...w }));
}
